import { differenceInSeconds, parseISO } from "date-fns";
import { toZonedTime, format } from "date-fns-tz";
import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge } from "react-bootstrap";
import classNames from "classnames";
import { Datasource } from "../models/Datasource";
import { WorkflowMode, WorkflowStatusModel } from "../models/Workflow";
import { BdaService, BdaServiceApplicationProperty, BdaServiceProperty } from "../models/BdaService";
import { ExecutionStatus } from "../models/generics";
import { DatasetFileType } from "../models/BdaDataset";
import { LinkProps } from "react-router-dom";

const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export type PortInfo = {
  id: string;
  type?: string;
  nodeId?: string;
  nodeName?: string;
  acceptedTypes?: string[];
  datasetType?: string;
  datasetMode?: string;
}

export const renderTopicLink = (topic: string) => {
  const url = window.__env.KAKFA_UI_TOPIC_MESSAGES_URL.replace("$TOPIC_NAME", topic);
  return (
    <a className="d-flex align-items-center" href={url} target="_blank" rel="noopener noreferrer">
      <FontAwesomeIcon icon={solid('comments')} className="me-1" />
      <span className="me-2">{topic}</span>
    </a>
  )
}

export type CustomBootstrapLinkType = React.ComponentClass<LinkProps> & keyof JSX.IntrinsicElements;

export function setObjectType<T>(object: any): object is T { return object !== undefined }

export function findApplicationProperties(service: BdaService) {
  return service.properties.filter(prop => prop.type === 'application') as BdaServiceApplicationProperty[];
}

export function getNodePortsPerTypes(properties: BdaServiceProperty[], portType: 'input' | 'output', assetType: string) {
  return properties
    .filter(prop => prop.key.startsWith(`${portType}-${assetType}`))
    .map(prop => {
      const portInfo: PortInfo = { id: prop.key, type: assetType };
      if (assetType === "dataset") {
        portInfo.datasetType = prop.extra?.datasetType;
        portInfo.datasetMode = prop.extra?.mode;

        if (portType === "input") {
          const columnsKey = prop.key.replace("dataset", "columns");
          portInfo.acceptedTypes = properties.find(prop => prop.key === columnsKey)?.defaultValue?.split(",") || [];
        }
      }
      return portInfo;
    });
}

export function getNodePortsFromProperties(service: BdaService) {
  const applicationProperties = findApplicationProperties(service);

  const inputDatasets = getNodePortsPerTypes(applicationProperties, 'input', 'dataset');
  const inputModels = getNodePortsPerTypes(applicationProperties, 'input', 'model');

  const outputDatasets = getNodePortsPerTypes(applicationProperties, 'output', 'dataset');
  const outputModels = getNodePortsPerTypes(applicationProperties, 'output', 'model');

  return {
    inputs: [...inputDatasets, ...inputModels],
    outputs: [...outputDatasets, ...outputModels]
  };
}

const formatInTimeZone = (date: Date, fmt: string, tz: string) => format(toZonedTime(date, tz), fmt, { timeZone: tz });

export function formatDate(date: string, format?: string) { return formatInTimeZone(parseISO(date + "Z"), format || 'dd/MM/yyyy HH:mm', currentTimeZone) }

export const modesOptions = [
  { label: 'Batch', value: WorkflowMode.BATCH, icon: solid('cog') },
  { label: 'Stream', value: WorkflowMode.STREAM, icon: solid('stream') },
];

export const iconsMap = new Map([
  ['INGESTION', solid('cloud-download-alt')],
  ['PREPARATION', solid('pen-square')],
  ['ANALYSIS', solid('chart-pie')],
  ['EVALUATION', solid('chart-line')],
  ['VISUALIZATION', solid('eye')],
  ['PROCESSING', solid('tools')],
]);

export const pageSizeOptions = [
  { label: "20 elements", value: 20 },
  { label: "40 elements", value: 40 },
  { label: "80 elements", value: 80 },
  { label: "100 elements", value: 100 },
];

export const accessLevelOptions = [
  { label: "Private", value: "PRIVATE", icon: solid('user-secret') },
  { label: "Team", value: "TEAM", icon: solid('users') },
  { label: "Public", value: "PUBLIC", icon: solid('globe-africa') },
];

export const datasourceOptions = [
  { label: 'Message Broker', value: 'messageBroker', icon: solid('comments') },
  { label: 'Object Storage', value: 'objectStorage', icon: solid('memory') },
  { label: 'Tabular Storage', value: 'database', icon: solid('database') },
  { label: 'FileSystem Storage', value: 'fileSystem', icon: solid('folder-tree') },
]

export function getConnectionDetails(datasource: Datasource, tableName?: string, datasetFileType?: DatasetFileType) {
  const overridesWrapper = datasource?.other?.find(other => other.key === 'vizConnector');
  const overrides = overridesWrapper?.value;
  const connectionDetails = {
    ...datasource,
    ...overrides,
    table: datasetFileType?.tableName || tableName,
    type: overrides?.driverType || datasource.driverType,
  };
  return connectionDetails;
}

export function createQueryDetails(connectionDetails: any) {
  const queryDetails = Object.entries(connectionDetails).map(([key, value]) => ({ [key]: value }));
  return { queryDetails };
}

export const getStatusIcon = (status: ExecutionStatus, noClass?: boolean) => {
  var icon, className = 'text-dark', spin = false;

  switch (status) {
    case ExecutionStatus.RUNNING:
      icon = solid('cog');
      spin = true;
      break;
    case ExecutionStatus.STOPPING:
      icon = solid('ban');
      spin = true;
      break;
    case ExecutionStatus.STOPPED:
      icon = solid('stop');
      break;
    default:
      icon = regular('clock');
      break;
    case ExecutionStatus.STARTING:
      icon = solid('spinner');
      spin = true;
      break;
    case ExecutionStatus.ERROR:
      icon = solid('exclamation-circle');
      className = 'text-danger';
      break;
    case ExecutionStatus.COMPLETED:
      icon = regular('check-circle');
      className = 'text-success';
      break;
  }

  return <FontAwesomeIcon icon={icon} spin={spin} className={classNames({ [className]: !noClass })} />
}

export const getStatusBadge = (status: ExecutionStatus) => {
  return (
    <Badge bg="none" className={`service-badge ${status}`}>
      {getStatusIcon(status, true)}
      <span className="ms-1">{status}</span>
    </Badge>
  );
}

export const fixProcedure = (procedure: any[], services: BdaService[]) => {
  return procedure.map(element => {
    if (element.type !== 'batch' && element.type !== 'stream') return element;

    const service = services.find(s => s.ref === element.id);

    return {
      ...element,
      data: {
        ...element.data,
        statusModel: service?.statusModel
      }
    };
  });
}

export const getPropertyIcon = (type: 'application' | 'tuning' | 'static') => {
  switch (type) {
    case 'application':
    default:
      return solid('play-circle');
    case 'tuning': return solid('wrench');
    case 'static': return solid('hdd');
  }
}

export const getSelectedColumns = (service: BdaService, inputDatasetKey?: string) => {
  return inputDatasetKey
    ? service.properties.find(prop => prop.key === inputDatasetKey.replace('dataset', 'columns'))?.value.split(",")
    : service.properties.find(prop => prop.key === "input-columns")?.value.split(",");
}

export const getExecutionTime = (statusModel: WorkflowStatusModel) => {
  const durationInSeconds = differenceInSeconds(new Date(statusModel.endTime), new Date(statusModel.initTime));
  const
    seconds = durationInSeconds % 60,
    minutes = durationInSeconds / 60,
    hours = durationInSeconds / 3600;

  var formatted = `${seconds}s`;
  if (minutes >= 1) formatted = `${Math.floor(minutes)}m ` + formatted;
  if (hours >= 1) formatted = `${Math.floor(hours)}h ` + formatted;

  return formatted;
}

export const notificationTypesOptions = [
  { label: "Application Status Changed", value: "BDA_APP_STATUS_CHANGE" },
  { label: "Application Asset Created", value: "BDA_APP_ASSET_CREATED" },
  // { label: "Application Information Edited", value: "BDA_APP_EDITED" },
];